import type {
  RedeemAccessCodeErrorCode,
  RedeemAccessCodeErrorDetails,
} from '@breakoutlearning/firebase-repository/cubits/accessCodeRedemption'
import type { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'

type TypedTranslations = ReturnType<typeof useTranslationTyped>['tt']

// support: end with "Still stuck? Email support@…"
export type AccessCodeError = {
  title: string
  body?: string
  support?: boolean
}

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`

/**
 * What went wrong and what to do about it (BL-5103). Details come only from
 * the Adonis endpoint; without them the copy stays general.
 */
export function accessCodeError(
  tt: TypedTranslations,
  errorCode: RedeemAccessCodeErrorCode,
  details?: RedeemAccessCodeErrorDetails
): AccessCodeError {
  const t =
    tt.pages.student.assignment.dialogs.purchase_or_enroll.access_code_errors
  switch (errorCode) {
    case 'INVALID_CODE':
      return {
        title: t.invalid_code.title(),
        body: t.invalid_code.body(),
        support: true,
      }
    case 'ALREADY_REDEEMED':
      if (details?.redeemedByYou)
        return {
          title: t.already_redeemed.title_by_you(),
          body: details.redeemedIn
            ? t.already_redeemed.body_by_you({ section: details.redeemedIn })
            : t.already_redeemed.body_by_you_no_section(),
          support: true,
        }
      return {
        title: t.already_redeemed.title(),
        body: t.already_redeemed.body(),
        support: true,
      }
    case 'EXPIRED':
      return {
        title: details?.expiresAt
          ? t.expired.title_with_date({
              date: DateTime.fromISO(details.expiresAt).toLocaleString(
                DateTime.DATE_MED
              ),
            })
          : t.expired.title(),
        body: t.expired.body(),
        support: true,
      }
    case 'PRICE_MISMATCH':
      return {
        title: t.price_mismatch.title(),
        body:
          details?.codePrice !== undefined && details.sectionPrice !== undefined
            ? t.price_mismatch.body_with_prices({
                codePrice: formatCents(details.codePrice),
                sectionPrice: formatCents(details.sectionPrice),
              })
            : t.price_mismatch.body(),
        support: true,
      }
    case 'ALREADY_HAS_PASS':
      return {
        title: t.already_has_pass.title(),
        body: t.already_has_pass.body(),
      }
    case 'SECTION_NOT_ELIGIBLE':
      return {
        title: t.section_not_eligible.title(),
        body: t.section_not_eligible.body(),
        support: true,
      }
    case 'RATE_LIMITED':
      return { title: t.rate_limited.title(), body: t.rate_limited.body() }
    case 'UNKNOWN':
      return { title: t.unknown.title(), body: t.unknown.body(), support: true }
  }
}
